import React, { Fragment, useEffect, useState } from 'react';
import moment from 'moment-timezone';
import { useSelector, useDispatch } from 'react-redux';
import * as R from 'ramda';
import { capitalize } from 'lodash';
import { GetApp } from '@material-ui/icons';
import {
  ArrayField,
  BooleanField,
  Button,
  Datagrid,
  Error,
  FunctionField,
  Loading,
  ReferenceManyField,
  SelectField,
  downloadCSV,
  Tab,
  toggleListItemExpand,
  useNotify,
  useQueryWithStore,
  Toolbar,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';

import {
  Box,
  Button as MuiButton,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  makeStyles,
  Typography,
  Grid,
} from '@material-ui/core';
import { Link } from 'react-router-dom';
import {
  DateRange,
  EventAvailable,
  AssignmentTurnedIn,
} from '@material-ui/icons';
import { orange, red } from '@material-ui/core/colors';

import { DateField, NavigableTabs, useBackend } from 'resources/common';
import { FullNameField } from 'resources/students/fields';
import { attendance } from 'data';
import { authServer } from 'auth/keycloak';
// Removed per-row queries in favor of batched data at parent level
// Replaced metrics fields with local FunctionField using prefetched data
import ZoomAttendanceRateFieldDaily from 'resources/students/fields/ZoomAttendanceRateFieldDaily';
import ZoomConnectionsField from 'resources/students/fields/ZoomConnectionsField';
import ZoomTotalMinutesField from 'resources/students/fields/ZoomTotalMinutesField';
import { hasDoubleSessions } from 'resources/cohorts_b2b/b2bSessions';

const OrderedDatagrid = ({ source, record, children }) => {
  const students = R.sort(
    (a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(
        `${b.first_name} ${b.last_name}`,
        'es',
        { sensitivity: 'base' }
      ),
    R.propOr([], source, record)
  );
  return (
    <Datagrid data={students} ids={students.map((_, i) => i)}>
      {children}
    </Datagrid>
  );
};

const OrderedStudentsDatagrid = ({ children, ...props }) => {
  return (
    <ReferenceManyField
      addLabel={false}
      source="id"
      reference="students"
      target="cohort"
      perPage={80}
      sort={{ field: 'first_name', order: 'ASC' }}
      {...props}
    >
      <Datagrid {...props}>{children}</Datagrid>
    </ReferenceManyField>
  );
};

const ExportAttendanceButton = ({
  students = [],
  minutesByStudent,
  totalClassMinutes,
  totalClassMinutesByStudent = {},
}) => {
  const exportAttendance = () => {
    const rows = (students || []).map((s) => {
      const totalMinutes = minutesByStudent?.[s.id] || 0;
      const rateWithAbsences = totalClassMinutes
        ? Math.min(100, Math.round((totalMinutes / totalClassMinutes) * 100))
        : 0;
      const totalForStudent = totalClassMinutesByStudent[s.id] || 0;
      const rateDaysPresent = totalForStudent
        ? Math.min(100, Math.round((totalMinutes / totalForStudent) * 100))
        : 0;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const totalHoursAttended = `${hours}h ${minutes}m`;
      const roundedUpHours = Math.ceil(totalMinutes / 60);
      return {
        full_name: `${s.first_name || ''} ${s.last_name || ''}`.trim(),
        student_id: s.id || '',
        cohort_id: s.cohort || '',
        email: s.email || '',
        total_minutes: totalMinutes,
        total_hours_attended: totalHoursAttended,
        rounded_up_hours: `${roundedUpHours} h`,
        attendance_rate_with_absences: `${rateWithAbsences}%`,
        attendance_rate_without_absences: `${rateDaysPresent}%`,
      };
    });
    jsonExport(
      rows,
      {
        headers: [
          'full_name',
          'email',
          'student_id',
          'cohort_id',
          'total_minutes',
          'total_hours_attended',
          'rounded_up_hours',
          'attendance_rate_with_absences',
          'attendance_rate_without_absences',
        ],
      },
      (error, csv) => {
        downloadCSV(csv, 'attendance_by_students');
      }
    );
  };

  return (
    <MuiButton
      variant="outlined"
      color="primary"
      startIcon={<GetApp />}
      onClick={exportAttendance}
    >
      Export Attendance CSV
    </MuiButton>
  );
};

const DayAttendanceList = ({ record, week, cohort }) => {
  const isZoomAttendanceEnabled = R.pathSatisfies(
    R.equals(true),
    ['feature_flags', 'automatic_attendance_enabled'],
    cohort
  );
  // Build a single list of student ids for attended+late (only these need zoom data)
  const presentStudents = React.useMemo(() => {
    const attended = R.pluck('id', R.propOr([], 'attended', record));
    const late = R.pluck('id', R.propOr([], 'late', record));
    return R.uniq([...attended, ...late]);
  }, [record]);

  // Prefetch zoom attendance for this day and present students
  const {
    data: dayZoomData,
    loading: dayZoomLoading,
    error: dayZoomError,
  } = useQueryWithStore(
    isZoomAttendanceEnabled && presentStudents.length
      ? {
          type: 'getManyReference',
          resource: 'zoom_attendance',
          payload: {
            search: [
              {
                $match: {
                  $and: [
                    { session_date: moment(record.id).format('YYYY-MM-DD') },
                    { student_id: { $in: presentStudents } },
                  ],
                },
              },
            ],
            pagination: { perPage: 2000, page: 1 },
            sort: { field: 'join_time', order: 'ASC' },
          },
        }
      : { type: 'noop' }
  );

  const zoomByStudent = React.useMemo(() => {
    const list = dayZoomData || [];
    return list.reduce((acc, rec) => {
      const sid = rec.student_id;
      acc[sid] = acc[sid] || [];
      acc[sid].push(rec);
      return acc;
    }, {});
  }, [dayZoomData]);

  // renderStudentTotalMinutes replaced by ZoomTotalMinutesField usage

  // renderStudentConnections replaced by ZoomConnectionsField usage
  return (
    <Fragment>
      {!R.isEmpty(R.propOr([], 'attended', record)) && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Attended
            </Typography>
            <ArrayField label="Students" source="attended" record={record}>
              <OrderedDatagrid source="attended" record={record}>
                <FullNameField sortable={false} />
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Total minutes"
                    render={(student) => (
                      <ZoomTotalMinutesField
                        date={record.id}
                        record={student}
                        isAbsent={false}
                        attendanceDataOverride={zoomByStudent[student.id] || []}
                        loadingOverride={dayZoomLoading}
                        errorOverride={dayZoomError}
                      />
                    )}
                    sortable={false}
                  />
                )}
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Zoom connections"
                    render={(student) => (
                      <ZoomConnectionsField
                        date={record.id}
                        record={student}
                        timezone={cohort?.timezone}
                        isAbsent={false}
                        attendanceDataOverride={zoomByStudent[student.id] || []}
                        loadingOverride={dayZoomLoading}
                        errorOverride={dayZoomError}
                      />
                    )}
                    sortable={false}
                  />
                )}
              </OrderedDatagrid>
            </ArrayField>
          </CardContent>
        </Card>
      )}
      {!R.isEmpty(R.propOr([], 'absent', record)) && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Absent
            </Typography>
            <ArrayField label="Students" source="absent" record={record}>
              <OrderedDatagrid source="absent" record={record}>
                <FullNameField sortable={false} />
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Total minutes"
                    render={() => '0 min'}
                    sortable={false}
                  />
                )}
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Zoom connections"
                    render={(student) => (
                      <ZoomConnectionsField
                        date={record.id}
                        record={student}
                        timezone={cohort?.timezone}
                        isAbsent={true}
                        attendanceDataOverride={[]}
                        loadingOverride={dayZoomLoading}
                        errorOverride={dayZoomError}
                      />
                    )}
                    sortable={false}
                  />
                )}
              </OrderedDatagrid>
            </ArrayField>
          </CardContent>
        </Card>
      )}
      {!R.isEmpty(R.propOr([], 'late', record)) && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Late
            </Typography>
            <ArrayField label="Students" source="late" record={record}>
              <OrderedDatagrid source="late" record={record}>
                <FullNameField sortable={false} />
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Total minutes"
                    render={(student) => (
                      <ZoomTotalMinutesField
                        date={record.id}
                        record={student}
                        isAbsent={false}
                        attendanceDataOverride={zoomByStudent[student.id] || []}
                        loadingOverride={dayZoomLoading}
                        errorOverride={dayZoomError}
                      />
                    )}
                    sortable={false}
                  />
                )}
                {isZoomAttendanceEnabled && (
                  <FunctionField
                    label="Zoom connections"
                    render={(student) => (
                      <ZoomConnectionsField
                        date={record.id}
                        record={student}
                        timezone={cohort?.timezone}
                        isAbsent={false}
                        attendanceDataOverride={zoomByStudent[student.id] || []}
                        loadingOverride={dayZoomLoading}
                        errorOverride={dayZoomError}
                      />
                    )}
                    sortable={false}
                  />
                )}
              </OrderedDatagrid>
            </ArrayField>
          </CardContent>
        </Card>
      )}
      {!R.isEmpty(R.propOr([], 'excused', record)) && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Excused
            </Typography>
            <ArrayField label="Students" source="excused" record={record}>
              <OrderedDatagrid source="excused" record={record}>
                <FullNameField sortable={false} />
              </OrderedDatagrid>
            </ArrayField>
          </CardContent>
        </Card>
      )}
    </Fragment>
  );
};

const AddFullAttendanceButton = ({ record, cohort, addFullAttendance }) => {
  const notify = useNotify();
  const [{ loaded, error, loading }, setCall] = useBackend();
  const handleAddFullAttendance = () => {
    setCall({
      url: '/attendance_b2b',
      method: 'POST',
      body: { date: record.id, id: cohort.id, session: record.session },
    });
  };
  useEffect(() => {
    if (error) {
      notify(`Could not update attendance`, 'warning');
    }
  }, [error, notify]);

  useEffect(() => {
    if (loaded && !error) {
      addFullAttendance(record.id, record.session);
      notify('Attendance Updated', 'info');
    }
  }, [error, loaded]); // eslint-disable-line

  const showButton = record.timeline !== 'after' && !record.filled;
  return showButton ? (
    <MuiButton
      onClick={handleAddFullAttendance}
      disabled={loading}
      startIcon={
        loading ? (
          <CircularProgress color="inherit" size={16} />
        ) : (
          <AssignmentTurnedIn />
        )
      }
      variant="outlined"
      color="primary"
      size="small"
    >
      All In
    </MuiButton>
  ) : null;
};

const AddDayAttendanceButton = ({ record, cohort }) => {
  const showButton = record.timeline !== 'after';
  const attendance = R.evolve({
    absent: R.pluck('id'),
    late: R.pluck('id'),
    excused: R.pluck('id'),
  })(record);
  return showButton ? (
    <Button
      component={Link}
      variant="outlined"
      to={{
        pathname: '/attendance_b2b/create',
        state: { cohort, attendance },
      }}
      label={attendance.filled ? 'Edit' : 'Add'}
    >
      {attendance.filled ? <DateRange /> : <EventAvailable />}
    </Button>
  ) : null;
};

const DaysAttendanceListRowStyle = (record, index, defaultStyle = {}) => {
  if (record.timeline === 'before') {
    return { ...defaultStyle, backgroundColor: 'rgba(0, 0, 0, 0.04)' };
  } else if (record.timeline === 'today') {
    return { ...defaultStyle, boxShadow: 'inset 5px 0px 0px 0px #3f51b5' };
  } else {
    return { ...defaultStyle, backgroundColor: 'white' };
  }
};

const DaysAttendanceList = ({ record, cohort, addFullAttendance }) => {
  return (
    <ArrayField source="days" record={record}>
      <Datagrid
        rowStyle={DaysAttendanceListRowStyle}
        expand={<DayAttendanceList record={record} cohort={cohort} />}
      >
        <DateField
          label="Day"
          source="id"
          sortable={false}
          format="dddd, Do MMMM YYYY"
        />
        {hasDoubleSessions(cohort) && (
          <FunctionField
            label="Session"
            render={(day) => capitalize(day.session)}
            sortable={false}
          />
        )}
        <FunctionField
          label="Attended"
          render={R.pipe(R.propOr([], 'attended'), R.length)}
          sortable={false}
        />
        <FunctionField
          label="Absences"
          render={R.pipe(R.propOr([], 'absent'), R.length)}
          sortable={false}
        />
        <FunctionField
          label="Late"
          render={R.pipe(R.propOr([], 'late'), R.length)}
          sortable={false}
        />
        <FunctionField
          label="Excused"
          render={R.pipe(R.propOr([], 'excused'), R.length)}
          sortable={false}
        />
        <BooleanField label="Filled" source="filled" sortable={false} />
        {authServer.isRoles(['pm', 'teacher', 'ta', 'campus_lead']) && (
          <AddDayAttendanceButton cohort={cohort} />
        )}
        {authServer.isRoles(['pm', 'teacher', 'ta', 'campus_lead']) && (
          <AddFullAttendanceButton
            cohort={cohort}
            addFullAttendance={
              addFullAttendance ? addFullAttendance(record.week) : null
            }
          />
        )}
      </Datagrid>
    </ArrayField>
  );
};

const ByDateTabPanelRowStyle = (record, index, defaultStyle = {}) => {
  if (record.timeline === 'today') {
    return { ...defaultStyle, boxShadow: 'inset 5px 0px 0px 0px #3f51b5' };
  } else if (record.timeline === 'before') {
    return { ...defaultStyle, backgroundColor: 'rgba(0, 0, 0, 0.04)' };
  } else {
    return { ...defaultStyle, backgroundColor: 'white' };
  }
};

const ByDateTabPanel = ({ record }) => {
  const { data, loading, error } = useQueryWithStore({
    type: 'getOne',
    resource: 'attendance_b2b',
    payload: { id: record.id },
  });
  const classes = makeStyles({ headerRow: { display: 'none' } })();

  const [attendance, setAttendance] = useState([]);

  const addFullAttendance = R.curry((week, date, session) => {
    const weekIndex = R.findIndex(R.propEq('week', week), attendance);
    const dayIndex = R.findIndex(
      R.both(R.propEq('id', date), R.propEq('session', session)),
      attendance[weekIndex].days
    );
    setAttendance(
      R.assocPath([weekIndex, 'days', dayIndex, 'filled'], true, attendance)
    );
  });

  useEffect(() => {
    if (data) {
      setAttendance(data.attendance);
    }
  }, [data]);

  const dispatch = useDispatch();
  const expanded = useSelector(
    R.pathOr([], ['admin', 'resources', 'cohorts_b2b', 'list', 'expanded'])
  );
  useEffect(() => {
    const currentWeek = R.find(R.propEq('timeline', 'today'), attendance);
    if (currentWeek) {
      if (!R.includes(currentWeek.id, expanded)) {
        dispatch(toggleListItemExpand('cohorts_b2b', currentWeek.id));
      }
    }
  }, [attendance]); // eslint-disable-line

  if (loading)
    return (
      <Box p="16px">
        <Loading />
      </Box>
    );
  if (error)
    return (
      <Box p="16px">
        <Error />
      </Box>
    );
  return (
    <Box>
      <Datagrid
        classes={{ headerRow: classes.headerRow }}
        data={R.indexBy(R.prop('id'), attendance)}
        ids={R.pluck('id', attendance)}
        selectedIds={[]}
        loading={loading}
        loaded={!loading}
        currentSort={{ field: 'id', order: 'ASC' }}
        rowStyle={ByDateTabPanelRowStyle}
        rowClick="expand"
        expand={
          <DaysAttendanceList
            cohort={record}
            addFullAttendance={addFullAttendance}
          />
        }
      >
        <FunctionField
          label="Week"
          render={(record) => `Week ${record.id}`}
          sortable={false}
        />
      </Datagrid>
    </Box>
  );
};

const AttendanceList = ({ record }) => (
  <ArrayField source="attendance" record={record}>
    <Datagrid>
      <DateField source="date" />
      <FunctionField
        source="session"
        render={(day) => capitalize(day.session)}
      />
      <SelectField source="attendance" choices={attendance} />
    </Datagrid>
  </ArrayField>
);

const StudentAttendanceList = ({
  record: student,
  cohort,
  weeks,
  loading,
  error,
  prefetchedAttendance,
  zoomSessions,
  loadingZoom,
  loadingSessions,
  timezone,
  attendanceIndex,
}) => {
  const isIn = (arr, studentId) =>
    Array.isArray(arr) && arr.some((s) => s.id === studentId);

  const buildStudentTimeline = (weeksData, studentId) => {
    const out = [];
    for (const week of weeksData || []) {
      for (const day of week.days || []) {
        if (!day || day.filled === false) continue;
        let status = null;
        if (isIn(day.attended, studentId)) status = 'attended';
        else if (isIn(day.absent, studentId)) status = 'absent';
        else if (isIn(day.late, studentId)) status = 'late';
        else if (isIn(day.excused, studentId)) status = 'excused';
        if (status) {
          out.push({
            date: day.id,
            session: day.session,
            week: week.week,
            status,
          });
        }
      }
    }
    return out.sort((a, b) => a.date.localeCompare(b.date));
  };

  const timeline = buildStudentTimeline(weeks || [], student?.id);
  const grouped = timeline.reduce(
    (acc, item) => {
      acc[item.status] = acc[item.status] || [];
      acc[item.status].push(item);
      return acc;
    },
    { attended: [], absent: [], late: [], excused: [] }
  );

  const attendedAndLate = [...grouped.attended, ...grouped.late];
  const attendedIdsFormatted = attendedAndLate.map((d) =>
    moment(d.date).format('YYYY-MM-DD')
  );

  // Build zoom attendance from prefetched data (no extra calls)
  const zoomDataLoading = !!loadingZoom;
  const zoomDataError = null;
  const studentId = student?.id;
  const zoomDataResp = React.useMemo(() => {
    const sid = String(studentId);
    const out = [];
    for (const d of attendedIdsFormatted) {
      const arr = attendanceIndex?.[`${sid}|${d}`];
      if (arr && arr.length) out.push(...arr);
    }
    return out;
  }, [attendanceIndex, studentId, attendedIdsFormatted]);

  const zoomByDate = React.useMemo(() => {
    const list = zoomDataResp || [];
    return list.reduce((acc, rec) => {
      const key = moment(rec.session_date).format('YYYY-MM-DD');
      acc[key] = acc[key] || [];
      acc[key].push(rec);
      return acc;
    }, {});
  }, [zoomDataResp]);

  const renderTotalMinutes = (date, isAbsent) => {
    if (isAbsent) return '0 min';
    if (zoomDataLoading)
      return (
        <Box display="flex" alignItems="center">
          <CircularProgress size={20} />
        </Box>
      );
    if (zoomDataError && zoomDataError.message !== 'No attendance data')
      return 'Error';
    const key = moment(date).format('YYYY-MM-DD');
    const records = zoomByDate[key] || [];
    if (R.isEmpty(records)) return '0 min';

    // Group by UUID and sum (same logic as By Date view)
    const minutesByUuid = records.reduce((acc, r) => {
      const uuid = r.zoom_meeting_uuid;
      if (!uuid) return acc;
      if (r.join_time && r.leave_time) {
        const start = moment(r.join_time);
        const end = moment(r.leave_time);
        const durationMinutes = Math.max(0, end.diff(start, 'minutes'));
        acc[uuid] = (acc[uuid] || 0) + durationMinutes;
      }
      return acc;
    }, {});

    const total = Object.values(minutesByUuid).reduce(
      (sum, minutes) => sum + minutes,
      0
    );
    return `${total} min`;
  };

  // renderConnections replaced by ZoomConnectionsField usage

  const hasAttended = grouped.attended.length > 0;
  const hasLate = grouped.late.length > 0;
  const hasAbsent = grouped.absent.length > 0;
  const hasExcused = grouped.excused.length > 0;

  return (
    <Fragment>
      {loading && (
        <Box p="16px">
          <Loading />
        </Box>
      )}
      {error && (
        <Box p="16px">
          <Error />
        </Box>
      )}
      {hasAttended && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Attended
            </Typography>
            <Datagrid
              data={R.indexBy(R.prop('date'), grouped.attended)}
              ids={R.pluck('date', grouped.attended)}
            >
              <FunctionField
                label="Date"
                render={(row) => (
                  <DateField
                    record={{ date: row.date }}
                    source="date"
                    format="dddd, Do MMMM YYYY"
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Total minutes"
                render={(row) => renderTotalMinutes(row.date, false)}
                sortable={false}
              />
              <FunctionField
                label="Zoom connections"
                render={(row) => (
                  <ZoomConnectionsField
                    date={row.date}
                    record={student}
                    timezone={cohort?.timezone}
                    isAbsent={false}
                    attendanceDataOverride={(
                      zoomByDate[moment(row.date).format('YYYY-MM-DD')] || []
                    ).filter((r) => r.student_id === student.id)}
                    loadingOverride={zoomDataLoading}
                    errorOverride={zoomDataError}
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Attendance Rate"
                render={(row) => (
                  <ZoomAttendanceRateFieldDaily
                    record={student}
                    date={row.date}
                    timezone={cohort?.timezone}
                    attendanceDataOverride={
                      zoomByDate[moment(row.date).format('YYYY-MM-DD')]
                    }
                    sessionsDataOverride={zoomSessions}
                    loadingOverride={zoomDataLoading || loadingSessions}
                    totalMinutesOverride={(function () {
                      const key = moment(row.date).format('YYYY-MM-DD');
                      const allRecords = zoomByDate[key] || [];
                      // Filter by specific student
                      const records = allRecords.filter(
                        (r) => r.student_id === student.id
                      );
                      // Group by UUID and sum (same logic as By Date view)
                      const minutesByUuid = records.reduce((acc, r) => {
                        const uuid = r.zoom_meeting_uuid;
                        if (!uuid) return acc;
                        if (r.join_time && r.leave_time) {
                          const start = moment(r.join_time);
                          const end = moment(r.leave_time);
                          const durationMinutes = Math.max(
                            0,
                            end.diff(start, 'minutes')
                          );
                          acc[uuid] = (acc[uuid] || 0) + durationMinutes;
                        }
                        return acc;
                      }, {});
                      return Object.values(minutesByUuid).reduce(
                        (sum, minutes) => sum + minutes,
                        0
                      );
                    })()}
                  />
                )}
                sortable={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      )}

      {hasLate && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Late
            </Typography>
            <Datagrid
              data={R.indexBy(R.prop('date'), grouped.late)}
              ids={R.pluck('date', grouped.late)}
            >
              <FunctionField
                label="Date"
                render={(row) => (
                  <DateField
                    record={{ date: row.date }}
                    source="date"
                    format="dddd, Do MMMM YYYY"
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Total minutes"
                render={(row) => renderTotalMinutes(row.date, false)}
                sortable={false}
              />
              <FunctionField
                label="Zoom connections"
                render={(row) => (
                  <ZoomConnectionsField
                    date={row.date}
                    record={student}
                    timezone={cohort?.timezone}
                    isAbsent={false}
                    attendanceDataOverride={(
                      zoomByDate[moment(row.date).format('YYYY-MM-DD')] || []
                    ).filter((r) => r.student_id === student.id)}
                    loadingOverride={zoomDataLoading}
                    errorOverride={zoomDataError}
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Attendance Rate"
                render={(row) => (
                  <ZoomAttendanceRateFieldDaily
                    record={student}
                    date={row.date}
                    timezone={cohort?.timezone}
                    attendanceDataOverride={
                      zoomByDate[moment(row.date).format('YYYY-MM-DD')]
                    }
                    sessionsDataOverride={zoomSessions}
                    loadingOverride={zoomDataLoading || loadingSessions}
                    totalMinutesOverride={(function () {
                      const key = moment(row.date).format('YYYY-MM-DD');
                      const allRecords = zoomByDate[key] || [];
                      // Filter by specific student
                      const records = allRecords.filter(
                        (r) => r.student_id === student.id
                      );
                      // Group by UUID and sum (same logic as By Date view)
                      const minutesByUuid = records.reduce((acc, r) => {
                        const uuid = r.zoom_meeting_uuid;
                        if (!uuid) return acc;
                        if (r.join_time && r.leave_time) {
                          const start = moment(r.join_time);
                          const end = moment(r.leave_time);
                          const durationMinutes = Math.max(
                            0,
                            end.diff(start, 'minutes')
                          );
                          acc[uuid] = (acc[uuid] || 0) + durationMinutes;
                        }
                        return acc;
                      }, {});
                      return Object.values(minutesByUuid).reduce(
                        (sum, minutes) => sum + minutes,
                        0
                      );
                    })()}
                  />
                )}
                sortable={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      )}

      {hasAbsent && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Absent
            </Typography>
            <Datagrid
              data={R.indexBy(R.prop('date'), grouped.absent)}
              ids={R.pluck('date', grouped.absent)}
            >
              <FunctionField
                label="Date"
                render={(row) => (
                  <DateField
                    record={{ date: row.date }}
                    source="date"
                    format="dddd, Do MMMM YYYY"
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Session"
                render={(row) =>
                  row.session === 'afternoon' ? 'Afternoon' : 'Morning'
                }
                sortable={false}
              />
              <FunctionField
                label="Attendance"
                render={(row) => 'Absent'}
                sortable={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      )}

      {hasExcused && (
        <Card style={{ margin: '15px 0px' }}>
          <CardContent>
            <Typography variant="h5" component="h3" gutterBottom>
              Excused
            </Typography>
            <Datagrid
              data={R.indexBy(R.prop('date'), grouped.excused)}
              ids={R.pluck('date', grouped.excused)}
            >
              <FunctionField
                label="Date"
                render={(row) => (
                  <DateField
                    record={{ date: row.date }}
                    source="date"
                    format="dddd, Do MMMM YYYY"
                  />
                )}
                sortable={false}
              />
              <FunctionField
                label="Session"
                render={(row) =>
                  row.session === 'afternoon' ? 'Afternoon' : 'Morning'
                }
                sortable={false}
              />
              <FunctionField
                label="Attendance"
                render={(row) => 'Excused'}
                sortable={false}
              />
            </Datagrid>
          </CardContent>
        </Card>
      )}
    </Fragment>
  );
};

const AttendanceChip = ({ record: student, attendanceType }) => {
  const amount = R.pipe(
    R.propOr([], 'attendance'),
    R.reduce((total, day) => {
      return day.attendance === attendanceType ? total + 1 : total;
    }, 0)
  )(student);
  const attendanceStyles = {
    absent: R.cond([
      [R.gt(2), R.always({})],
      [R.equals(2), R.always({ color: 'black', backgroundColor: red[200] })],
      [R.lt(2), R.always({ color: 'white', backgroundColor: red[900] })],
    ]),
    late: R.cond([
      [R.gte(2), R.always({})],
      [R.lte(6), R.always({ color: 'white', backgroundColor: orange[900] })],
      [R.T, R.always({ color: 'black', backgroundColor: orange[200] })],
    ]),
    excused: R.always({}),
  };
  return (
    <Chip style={attendanceStyles[attendanceType](amount)} label={amount} />
  );
};

const ByStudentTabPanel = ({ basePath, record }) => {
  const isZoomAttendanceEnabled = R.pathSatisfies(
    R.equals(true),
    ['feature_flags', 'automatic_attendance_enabled'],
    record
  );

  const { data: studentsData } = useQueryWithStore({
    type: 'getManyReference',
    resource: 'students',
    payload: {
      target: 'cohort',
      id: record.id,
      pagination: { perPage: 200, page: 1 },
      sort: { field: 'first_name', order: 'ASC' },
    },
  });

  const defaultStyle = (
    <Box>
      <ReferenceManyField
        addLabel={false}
        source="id"
        reference="students"
        target="cohort"
        perPage={80}
        record={record}
        basePath={basePath}
      >
        <Datagrid rowClick="expand" expand={<AttendanceList />}>
          <FullNameField />
          <AttendanceChip label="Absent" attendanceType="absent" />
          <AttendanceChip label="Late" attendanceType="late" />
          <AttendanceChip label="Excused" attendanceType="excused" />
        </Datagrid>
      </ReferenceManyField>
    </Box>
  );

  const {
    data: weeksResp,
    loading: weeksLoading,
    error: weeksError,
  } = useQueryWithStore({
    type: 'getOne',
    resource: 'attendance_b2b',
    payload: { id: record.id },
  });

  const weeks = R.propOr([], 'attendance', weeksResp);

  // Prefetch all zoom attendance for this cohort (batched)
  const {
    data: cohortZoomAttendance,
    loading: cohortZoomLoading,
    error: cohortZoomError,
  } = useQueryWithStore(
    isZoomAttendanceEnabled
      ? {
          type: 'getManyReference',
          resource: 'zoom_attendance',
          payload: {
            search: [
              {
                $match: {
                  $and: [{ cohort_id: record.id }],
                },
              },
            ],
            pagination: { perPage: 5000, page: 1 },
            sort: { field: 'join_time', order: 'ASC' },
          },
        }
      : { type: 'noop' }
  );

  // Prefetch all zoom sessions for this cohort (batched)
  const {
    data: cohortZoomSessions,
    loading: cohortSessionsLoading,
    error: cohortSessionsError,
  } = useQueryWithStore(
    isZoomAttendanceEnabled
      ? {
          type: 'getManyReference',
          resource: 'zoom_sessions',
          payload: {
            search: [
              {
                $match: {
                  $and: [{ cohort_id: record.id }],
                },
              },
            ],
            pagination: { perPage: 2000, page: 1 },
          },
        }
      : { type: 'noop' }
  );

  const minutesByStudent = React.useMemo(() => {
    const list = cohortZoomAttendance || [];
    // Group by student_id, then by uuid, then sum (same logic as By Date view)
    const groupedByStudent = list.reduce((acc, rec) => {
      const sid = rec.student_id;
      const uuid = rec.zoom_meeting_uuid;
      if (!sid || !uuid) return acc;

      const hasDuration =
        typeof rec.duration === 'number' && !Number.isNaN(rec.duration);
      let inc = 0;
      if (hasDuration) {
        inc = Math.max(0, rec.duration);
      } else if (rec.join_time && rec.leave_time) {
        const start = moment(rec.join_time);
        const end = moment(rec.leave_time);
        inc = Math.max(0, end.diff(start, 'minutes'));
      }

      // Initialize student if not exists
      if (!acc[sid]) acc[sid] = {};
      // Initialize uuid if not exists
      if (!acc[sid][uuid]) acc[sid][uuid] = 0;
      // Add minutes
      acc[sid][uuid] += inc;
      return acc;
    }, {});

    // Sum all minutes per student
    return Object.keys(groupedByStudent).reduce((result, studentId) => {
      result[studentId] = Object.values(groupedByStudent[studentId]).reduce(
        (sum, minutes) => sum + minutes,
        0
      );
      return result;
    }, {});
  }, [cohortZoomAttendance]);

  // Build index: key `${student_id}|${session_date}` => records[]
  const attendanceByStudentDate = React.useMemo(() => {
    const list = cohortZoomAttendance || [];
    return list.reduce((acc, rec) => {
      const sid = String(rec.student_id);
      const dateKey = moment(rec.session_date).format('YYYY-MM-DD');
      const key = `${sid}|${dateKey}`;
      (acc[key] = acc[key] || []).push(rec);
      return acc;
    }, {});
  }, [cohortZoomAttendance]);

  const totalClassMinutes = React.useMemo(() => {
    const list = cohortZoomSessions || [];
    return list.reduce((sum, session) => {
      return sum + (session.duration || 0);
    }, 0);
  }, [cohortZoomSessions]);

  // Map sessions by uuid for quick lookup when computing daily rate without extra queries
  const sessionByUuid = React.useMemo(() => {
    const list = cohortZoomSessions || [];
    return list.reduce((acc, s) => {
      if (s && s.uuid) acc[s.uuid] = s;
      return acc;
    }, {});
  }, [cohortZoomSessions]);

  // Calculate total class minutes per student (excluding absent/excused days)
  const totalClassMinutesByStudent = React.useMemo(() => {
    if (!weeks || !cohortZoomSessions) return {};

    const sessionsByDate = (cohortZoomSessions || []).reduce((acc, session) => {
      const dateKey = moment(session.start_time).format('YYYY-MM-DD');
      acc[dateKey] = acc[dateKey] || [];
      acc[dateKey].push(session);
      return acc;
    }, {});

    const result = {};
    const students = studentsData || [];

    students.forEach((student) => {
      let totalMinutesForStudent = 0;

      weeks.forEach((week) => {
        const days = week.days || [];
        days.forEach((day) => {
          if (!day || !day.filled) return;

          const dateKey = moment(day.id).format('YYYY-MM-DD');
          const isPresent =
            (day.attended || []).some((s) => s.id === student.id) ||
            (day.late || []).some((s) => s.id === student.id);

          if (isPresent) {
            const sessionsForDay = sessionsByDate[dateKey] || [];
            const dayMinutes = sessionsForDay.reduce(
              (sum, s) => sum + (s.duration || 0),
              0
            );
            totalMinutesForStudent += dayMinutes;
          }
        });
      });

      result[student.id] = totalMinutesForStudent;
    });

    return result;
  }, [weeks, cohortZoomSessions, studentsData]);

  const zoomAttendanceStyle = (
    <Box>
      <OrderedStudentsDatagrid
        record={record}
        basePath={basePath}
        rowClick="expand"
        expand={
          <StudentAttendanceList
            cohort={record}
            weeks={weeks}
            loading={weeksLoading}
            error={weeksError}
            zoomSessions={cohortZoomSessions}
            prefetchedAttendance={cohortZoomAttendance}
            loadingZoom={cohortZoomLoading}
            loadingSessions={cohortSessionsLoading}
            timezone={record?.timezone}
            attendanceIndex={attendanceByStudentDate}
            sessionByUuid={sessionByUuid}
          />
        }
      >
        <FullNameField />
        {isZoomAttendanceEnabled && (
          <FunctionField
            label="Total hours attended"
            render={(student) => {
              if (cohortZoomLoading)
                return (
                  <Box display="flex" alignItems="center">
                    <CircularProgress size={20} />
                  </Box>
                );
              if (
                cohortZoomError &&
                cohortZoomError.message !== 'No attendance data'
              )
                return 'Error';
              const totalMinutes = minutesByStudent[student.id] || 0;
              const hours = Math.floor(totalMinutes / 60);
              const minutes = totalMinutes % 60;
              const totalCohortHours = Math.ceil((totalClassMinutes || 0) / 60);
              return `${hours}h ${minutes}m / ${totalCohortHours} h`;
            }}
          />
        )}

        {isZoomAttendanceEnabled && (
          <FunctionField
            label="Attendance rate (with absences)"
            render={(student) => {
              if (cohortZoomLoading || cohortSessionsLoading)
                return (
                  <Box display="flex" alignItems="center">
                    <CircularProgress size={20} />
                  </Box>
                );
              if (
                (cohortZoomError &&
                  cohortZoomError.message !== 'No attendance data') ||
                cohortSessionsError
              )
                return 'Error';
              const attended = minutesByStudent[student.id] || 0;
              if (!totalClassMinutes) return '0%';
              const pct = Math.min(
                100,
                Math.round((attended / totalClassMinutes) * 100)
              );
              return `${pct}%`;
            }}
          />
        )}

        {isZoomAttendanceEnabled && (
          <FunctionField
            label="Attendance rate (without absences)"
            render={(student) => {
              if (cohortZoomLoading || cohortSessionsLoading)
                return (
                  <Box display="flex" alignItems="center">
                    <CircularProgress size={20} />
                  </Box>
                );
              if (
                (cohortZoomError &&
                  cohortZoomError.message !== 'No attendance data') ||
                cohortSessionsError
              )
                return 'Error';
              const attended = minutesByStudent[student.id] || 0;
              const totalForStudent =
                totalClassMinutesByStudent[student.id] || 0;
              if (!totalForStudent) return '0%';
              const pct = Math.min(
                100,
                Math.round((attended / totalForStudent) * 100)
              );
              return `${pct}%`;
            }}
          />
        )}

        <AttendanceChip label="Absent" attendanceType="absent" />
        <AttendanceChip label="Late" attendanceType="late" />
        <AttendanceChip label="Excused" attendanceType="excused" />
      </OrderedStudentsDatagrid>
      <Toolbar>
        <Grid container spacing={1} direction="row">
          <Grid container item xs={12} spacing={2} justify="flex-end">
            <Grid item>
              <ExportAttendanceButton
                students={studentsData || []}
                minutesByStudent={minutesByStudent}
                totalClassMinutes={totalClassMinutes}
                totalClassMinutesByStudent={totalClassMinutesByStudent}
              />
            </Grid>
          </Grid>
        </Grid>
      </Toolbar>
    </Box>
  );
  return (
    <Box>{isZoomAttendanceEnabled ? zoomAttendanceStyle : defaultStyle}</Box>
  );
};

const AttendanceTab = (props) => {
  const classes = makeStyles({
    selected: { background: 'rgb(201, 206, 234)' },
  })();

  return (
    <NavigableTabs
      {...props}
      variant="fullWidth"
      matchUrl={R.pipe(R.split('/'), R.take(6), R.join('/'))}
    >
      <Tab
        key="calendar"
        label="By Date"
        path="calendar"
        classes={{ selected: classes.selected }}
      >
        <ByDateTabPanel />
      </Tab>
      <Tab
        key="students"
        label="By Students"
        path="students"
        classes={{ selected: classes.selected }}
      >
        <ByStudentTabPanel />
      </Tab>
    </NavigableTabs>
  );
};

export default AttendanceTab;

// Place the export button at the end of the tab (By Students)
export const AttendanceTabFooter = ({ record }) => (
  <Box p={2} display="flex" justifyContent="flex-end">
    <ExportAttendanceButton record={record} />
  </Box>
);
